import React, { useEffect, useMemo, useState } from 'react';
import Card from '../../components/card/Card';
import Icon from '../../components/icon/Icon';
import theme from '../../styles/theme';
import { useToast } from '../../components/toast/toast';
import EarlyCancellationDateModal from '../../components/dialogs/EarlyCancellationDateModal';
import EarlyCancellationConfirmModal from '../../components/dialogs/EarlyCancellationConfirmModal';
import {
  earlyCancellation,
  earlyCancellationInstallment,
  getLoanInstallments,
} from '../../services/GuaranteesService';
import {
  countPendingInstallments,
  isInstallmentEarlyCancellationEligible,
  isInstrumentEarlyCancellationEligible,
  LoanEarlyCancellationPreview,
  previewLoanEarlyCancellation,
  resolveEarlyCancellationInstrumentType,
  resolveOperationTypeLabel,
} from '../../utils/earlyCancellationUtils';

/** Figma action outline — warning palette (marron/cobre), not client primary/secondary-1 */
const EARLY_CANCELLATION_ACTION_STYLE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '12px 16px',
  borderRadius: theme.borderRadius['border-radius-3'],
  border: `1px solid ${theme.colors.warning.border.regular}`,
  backgroundColor: 'transparent',
  color: theme.colors.warning.text.regular,
  fontWeight: 700,
  cursor: 'pointer',
};

interface EarlyCancellationActionsProps {
  data: any;
  guaranteeType: 'loan' | 'check' | 'promissory_note' | 'commercial_endorsement';
  onSuccess?: () => void;
  installmentRefreshKey?: number;
}

const EarlyCancellationActions: React.FC<EarlyCancellationActionsProps> = ({
  data,
  guaranteeType,
  onSuccess,
  installmentRefreshKey = 0,
}) => {
  const [dateModalOpen, setDateModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [loanInstallments, setLoanInstallments] = useState<any[]>([]);
  const [loanEligible, setLoanEligible] = useState(false);

  const monetizationStatusTypeId =
    data?.monetization_status_type_id ?? data?.monetizationStatusTypeId;
  const loanInternalId = data?.id;
  const loanIdGlobal = data?.id_global ?? data?.idGlobal;
  const isLoan = guaranteeType === 'loan';
  const mode = isLoan ? 'loan' : 'instrument';
  const instrumentId = isLoan ? loanInternalId : data?.id;

  const normalizedInstallments = useMemo(
    () =>
      loanInstallments.map((row: any) => ({
        due_date: row.due_date ?? row.dueDate,
        status_type: row.status_type ?? row.statusType,
        original_due_date: row.original_due_date ?? row.originalDueDate,
      })),
    [loanInstallments],
  );

  const totalPendingCount = useMemo(
    () => countPendingInstallments(normalizedInstallments),
    [normalizedInstallments],
  );

  useEffect(() => {
    if (!isLoan || !loanIdGlobal) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await getLoanInstallments(String(loanIdGlobal));
        if (cancelled) return;
        if (response.success && 'data' in response) {
          const list = response.data.content || response.data || [];
          setLoanInstallments(list);
          const hasPending = list.some((row: any) =>
            isInstallmentEarlyCancellationEligible({
              statusType: row.status_type ?? row.statusType,
              originalDueDate: row.original_due_date ?? row.originalDueDate,
            }),
          );
          setLoanEligible(Number(monetizationStatusTypeId) === 2 && hasPending);
        }
      } catch {
        if (!cancelled) setLoanEligible(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isLoan, loanIdGlobal, monetizationStatusTypeId, installmentRefreshKey]);

  const showButton = useMemo(() => {
    if (isLoan) return loanEligible;
    return isInstrumentEarlyCancellationEligible({
      monetizationStatusTypeId: Number(monetizationStatusTypeId),
      statusType: data?.status_type,
      originalDueDate: data?.original_due_date,
    });
  }, [
    isLoan,
    loanEligible,
    monetizationStatusTypeId,
    data?.status_type,
    data?.original_due_date,
  ]);

  const maxDueDate = data?.due_date || '';

  const loanPreview: LoanEarlyCancellationPreview | null = useMemo(() => {
    if (!isLoan || !selectedDate) return null;
    return previewLoanEarlyCancellation(normalizedInstallments, selectedDate);
  }, [isLoan, selectedDate, normalizedInstallments]);

  const handleContinue = (date: string) => {
    setSelectedDate(date);
    setDateModalOpen(false);
    setConfirmModalOpen(true);
  };

  const handleConfirm = async () => {
    if (!instrumentId) return;
    setLoading(true);
    try {
      const type = resolveEarlyCancellationInstrumentType(guaranteeType);
      const response = await earlyCancellation(type, Number(instrumentId), selectedDate);
      if (response?.success === false) {
        throw new Error(response?.message || 'Error');
      }
      useToast.success({
        title: 'Cancelación anticipada registrada',
        id: 'early-cancellation-toast',
      });
      setConfirmModalOpen(false);
      window.setTimeout(() => onSuccess?.(), 0);
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'No se pudo registrar la cancelación anticipada';
      useToast.error({
        title: message,
        id: 'early-cancellation-toast',
      });
    } finally {
      setLoading(false);
    }
  };

  const showModals = showButton || dateModalOpen || confirmModalOpen;

  return (
    <>
      {showButton && (
        <Card display="flex" margin={{ top: '3', bottom: '3' }}>
          <button
            type="button"
            onClick={() => setDateModalOpen(true)}
            title="Cancelación anticipada"
            style={EARLY_CANCELLATION_ACTION_STYLE}
          >
            <Icon name="cancel_circle_outline" variant="warning" width={20} height={20} />
            Cancelación anticipada
          </button>
        </Card>
      )}

      {showModals && (
        <>
          <EarlyCancellationDateModal
            isOpen={dateModalOpen}
            onClose={() => {
              setDateModalOpen(false);
            }}
            onContinue={handleContinue}
            maxDueDate={maxDueDate}
            mode={mode}
            avalNumber={data?.id_global}
            companyName={data?.company_name}
            operationType={resolveOperationTypeLabel(data)}
            currentDueDate={data?.due_date}
            loanInstallments={normalizedInstallments}
            totalPendingCount={totalPendingCount}
          />

          <EarlyCancellationConfirmModal
            isOpen={confirmModalOpen}
            onClose={() => setConfirmModalOpen(false)}
            onConfirm={handleConfirm}
            isLoading={loading}
            selectedDate={selectedDate}
            mode={mode}
            avalNumber={data?.id_global}
            loanPreview={isLoan ? loanPreview : null}
          />
        </>
      )}
    </>
  );
};

export default EarlyCancellationActions;
